/**
 * Rank a column of numeric values into AntD tag colors so the
 * Infrastructure Comparison table can highlight the best/worst rows.
 *
 * Rules (driven by the user spec):
 *   - 1 distinct (non-null) value  -> every row gets `default` (no color).
 *   - 2 distinct values            -> green (best), red (worst).
 *   - 3 distinct values            -> green, orange, red.
 *   - 4 distinct values            -> green, gold, orange, red.
 *   - null / undefined values      -> `default`.
 *   - Equal values share the same rank/color (a tie at "best" produces
 *     two greens, etc.).
 *
 * Returns a `Map<value, color>` so callers look up colors by the row's
 * actual value rather than its position. This is important because AntD's
 * `Table` `render(value, record, index)` callback passes the *displayed*
 * index, which shifts when the user clicks a column sort header — using
 * a value-keyed map keeps each row's color stable across re-sorts.
 */

export const DEFAULT_COLOR = 'default';

// Palettes indexed by distinct-value count. Best -> worst, left -> right.
const PALETTES = {
  1: [DEFAULT_COLOR],
  2: ['green', 'red'],
  3: ['green', 'orange', 'red'],
  4: ['green', 'gold', 'orange', 'red'],
};

/**
 * @param {Array<number|null|undefined>} values
 * @param {Object} [options]
 * @param {boolean} [options.lowerIsBetter=true] - true for latency / cost,
 *        false for throughput.
 * @returns {Map<number, string>} Map from each distinct value to its AntD
 *        color name. Look up with `.get(value) ?? DEFAULT_COLOR`.
 */
export function rankColors(values, { lowerIsBetter = true } = {}) {
  const distinctSorted = Array.from(
    new Set(values.filter((v) => v !== null && v !== undefined && !Number.isNaN(v)))
  ).sort((a, b) => (lowerIsBetter ? a - b : b - a));

  const valueToColor = new Map();
  const palette = PALETTES[distinctSorted.length];
  if (!palette) return valueToColor;

  distinctSorted.forEach((v, idx) => valueToColor.set(v, palette[idx]));
  return valueToColor;
}
